import { useEffect, useRef } from 'react';
import { usePlayer } from '../context/PlayerContext';
import { toast } from 'sonner';

let ytApiPromise = null;
const loadYouTubeApi = () => {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
  return ytApiPromise;
};

/**
 * Renders the persistent YouTube IFrame player.
 * Controlled entirely by PlayerContext. Uses refs to avoid stale-closure
 * races between the async YT API load and rapid currentSong state changes —
 * this is what previously caused the display to be out of sync with what
 * was actually playing.
 */
const YouTubePlayer = ({ visible = true }) => {
  const {
    currentSong, isPlaying, playerRef, onSongEnd,
    setCurrentTime, setDuration, setIsPlaying,
  } = usePlayer();
  const containerRef = useRef(null);
  const ytPlayerRef = useRef(null);
  const readyRef = useRef(false);
  const currentVidRef = useRef(null);
  const currentSongRef = useRef(currentSong);
  const isPlayingRef = useRef(isPlaying);
  const onSongEndRef = useRef(onSongEnd);
  const reconcileRef = useRef(null);
  const pollRef = useRef(null);

  // Keep refs pointed at the latest values so async callbacks never see stale state.
  useEffect(() => { currentSongRef.current = currentSong; }, [currentSong]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { onSongEndRef.current = onSongEnd; }, [onSongEnd]);

  // Initialize player exactly once.
  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !containerRef.current || ytPlayerRef.current) return;
      ytPlayerRef.current = new YT.Player(containerRef.current, {
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 1,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          controls: 0,
          disablekb: 1,
          fs: 0,
        },
        events: {
          onReady: (e) => {
            readyRef.current = true;
            playerRef.current = e.target;
            reconcile();
          },
          onStateChange: (e) => {
            const state = e.data;
            if (state === YT.PlayerState.ENDED) {
              onSongEndRef.current && onSongEndRef.current();
            } else if (state === YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              const d = e.target.getDuration();
              if (d) setDuration(d);
            } else if (state === YT.PlayerState.PAUSED) {
              setIsPlaying(false);
            }
          },
          onError: (e) => {
            // Only auto-skip when the video is genuinely unavailable —
            // 2 (invalid id), 100 (not found), 101/150 (embedding blocked).
            // Silence transient HTML5 errors (5) that recover on their own.
            const code = e && e.data;
            const unavailable = code === 2 || code === 100 || code === 101 || code === 150;
            if (!unavailable) return;
            const song = currentSongRef.current;
            toast.error('This one is unavailable', {
              description: song ? `Skipping "${song.title.slice(0, 60)}"` : 'Skipping…',
              duration: 3500,
            });
            onSongEndRef.current && onSongEndRef.current();
          },
        },
      });
    });

    // Reconcile the iframe with the latest currentSong + isPlaying refs.
    // Safe to call before or after onReady — we early-return until ready.
    const reconcile = () => {
      const yt = ytPlayerRef.current;
      if (!yt || !readyRef.current) return;
      const song = currentSongRef.current;
      if (!song) {
        try { yt.stopVideo(); } catch (e) { /* noop */ }
        currentVidRef.current = null;
        return;
      }
      if (currentVidRef.current !== song.youtube_video_id) {
        currentVidRef.current = song.youtube_video_id;
        try {
          yt.loadVideoById({ videoId: song.youtube_video_id });
        } catch (e) { /* noop */ }
      }
      try {
        if (isPlayingRef.current) yt.playVideo(); else yt.pauseVideo();
      } catch (e) { /* noop */ }
    };
    reconcileRef.current = reconcile;

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever currentSong or isPlaying flips, ask the iframe to catch up.
  useEffect(() => {
    if (reconcileRef.current) reconcileRef.current();
  }, [currentSong, isPlaying]);

  // Poll current time
  useEffect(() => {
    pollRef.current = setInterval(() => {
      const yt = ytPlayerRef.current;
      if (yt && typeof yt.getCurrentTime === 'function') {
        try {
          setCurrentTime(yt.getCurrentTime() || 0);
          const d = yt.getDuration();
          if (d) setDuration(d);
        } catch (e) { /* noop */ }
      }
    }, 500);
    return () => clearInterval(pollRef.current);
  }, [setCurrentTime, setDuration]);

  return (
    <div
      data-testid="youtube-player-wrapper"
      className={`w-full h-full ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none absolute'}`}
      style={{ position: visible ? 'relative' : 'absolute', width: visible ? '100%' : '1px', height: visible ? '100%' : '1px', overflow: 'hidden' }}
    >
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
};

export default YouTubePlayer;
